import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { Calendar, Clock, Users, Mail, Phone, User, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reservation = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    people: '2',
    experience_type: 'Tour Clásico',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/reservations`, formData);
      
      if (response.data) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          people: '2',
          experience_type: 'Tour Clásico',
          message: ''
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      setSubmitStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const experiences = [
    'Tour Clásico',
    'Experiencia Gourmet',
    'Experiencia Cosecha',
    'Restaurant MonfrÀ',
    'Otro'
  ];

  return (
    <section id="reservation" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Hacé tu Reserva
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{background: 'linear-gradient(90deg, #D4AF37 0%, #C9A961 100%)'}}></div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Completá el formulario y nos pondremos en contacto para confirmar tu visita
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="grid md:grid-cols-5">
              {/* Left Sidebar */}
              <div className="md:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-white border-l-4" style={{borderLeftColor: '#D4AF37'}}>
                <h3 className="text-2xl font-bold mb-6" style={{color: '#D4AF37'}}>Información de Contacto</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <Phone className="h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Teléfono</p>
                      <p className="text-gray-300">+598 2345 6789</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Mail className="h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Email</p>
                      <p className="text-gray-300">info@bodegaspinoglio.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Calendar className="h-6 w-6 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Horarios</p>
                      <p className="text-gray-300">Martes a Domingo</p>
                      <p className="text-gray-300">10:00 - 18:00</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 p-4 backdrop-blur-sm rounded-xl" style={{background: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)'}}>
                  <p className="text-sm" style={{color: '#F4E4C1'}}>
                    ⭐ Reservas con anticipación recomendadas
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="md:col-span-3 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                      <User className="h-4 w-4" />
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                      placeholder="Tu nombre"
                      data-testid="reservation-name-input"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Mail className="h-4 w-4" />
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                        placeholder="tu@email.com"
                        data-testid="reservation-email-input"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Phone className="h-4 w-4" />
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                        placeholder="+598 99 123 456"
                        data-testid="reservation-phone-input"
                      />
                    </div>
                  </div>

                  {/* Date, Time & People */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Calendar className="h-4 w-4" />
                        Fecha *
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                        data-testid="reservation-date-input"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Clock className="h-4 w-4" />
                        Hora *
                      </label>
                      <input
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                        data-testid="reservation-time-input"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                        <Users className="h-4 w-4" />
                        Personas *
                      </label>
                      <select
                        name="people"
                        value={formData.people}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                        data-testid="reservation-people-select"
                      >
                        {[...Array(20)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Experience Type */}
                  <div>
                    <label className="text-gray-700 font-medium mb-2 block">
                      Tipo de Experiencia *
                    </label>
                    <select
                      name="experience_type"
                      value={formData.experience_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      style={{'--tw-ring-color': '#D4AF37'}}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                      onBlur={(e) => e.target.style.boxShadow = 'none'}
                      data-testid="reservation-experience-select"
                    >
                      {experiences.map((exp) => (
                        <option key={exp} value={exp}>{exp}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="text-gray-700 font-medium mb-2 block">
                      Mensaje (opcional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-700 focus:border-transparent transition-all resize-none"
                      placeholder="Comentarios adicionales..."
                      data-testid="reservation-message-textarea"
                    />
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white px-6 py-4 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                    style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    data-testid="reservation-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Enviar Reserva
                      </>
                    )}
                  </motion.button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 bg-green-50 text-green-800 p-4 rounded-lg"
                      data-testid="reservation-success-message"
                    >
                      <CheckCircle className="h-5 w-5" />
                      <span>¡Reserva enviada exitosamente! Nos contactaremos pronto.</span>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 text-red-800 p-4 rounded-lg"
                      data-testid="reservation-error-message"
                    >
                      Error al enviar la reserva. Por favor, intenta nuevamente.
                    </motion.div>
                  )}
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Reservation;
